import {
  checkForUpdatedBuild,
  isChunkLoadError,
  reconcileFreshBuildQueryParam,
  recoverFromStaleBuild,
} from './staleBuildGuard';

const getRuntimeWindow = () => (typeof window !== 'undefined' ? window : undefined);
const getRuntimeDocument = () => (typeof document !== 'undefined' ? document : undefined);
const getRuntimeSessionStorage = () => getRuntimeWindow()?.sessionStorage;

const scrollWindowInstantly = (position, { windowObject, documentObject }) => {
  const root = documentObject.documentElement;
  const previousBehavior = root.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  windowObject.scrollTo(0, position);

  windowObject.requestAnimationFrame(() => {
    if (previousBehavior) {
      root.style.scrollBehavior = previousBehavior;
    } else {
      root.style.removeProperty('scroll-behavior');
    }
  });
};

const clearSavedTabScrollState = (sessionStorageObject) => {
  sessionStorageObject.removeItem('scroll_position_before_hide');
  sessionStorageObject.removeItem('tab_hidden_at');
};

const restoreSavedScrollPosition = (deps) => {
  const {
    sessionStorageObject,
    windowObject,
    documentObject,
  } = deps;

  const savedScrollPosition = sessionStorageObject.getItem('scroll_position_before_hide');
  if (!savedScrollPosition) {
    clearSavedTabScrollState(sessionStorageObject);
    return;
  }

  const position = parseInt(savedScrollPosition, 10);
  if (!Number.isNaN(position) && position > 0) {
    scrollWindowInstantly(position, { windowObject, documentObject });
  }
  clearSavedTabScrollState(sessionStorageObject);
};

export function installAppRuntime({
  windowObject = getRuntimeWindow(),
  documentObject = getRuntimeDocument(),
  sessionStorageObject = getRuntimeSessionStorage(),
  checkForUpdatedBuildImpl = checkForUpdatedBuild,
  recoverFromStaleBuildImpl = recoverFromStaleBuild,
} = {}) {
  if (!windowObject || !documentObject || !sessionStorageObject) {
    return () => {};
  }

  reconcileFreshBuildQueryParam({
    locationObject: windowObject.location,
    historyObject: windowObject.history,
    sessionStorageObject,
  });

  windowObject.__APP_RUNNING = true;
  windowObject.__APP_STATE = {
    isVisible: !documentObject.hidden,
    lastVisibilityChange: Date.now(),
    pageLoaded: Date.now(),
  };

  let visibilityChangeTimeout;

  const handleChunkLoadIssue = async (reason) => {
    windowObject.__LAST_CHUNK_LOAD_ERROR = {
      pathname: windowObject.location.pathname,
      reason: String(reason?.message || reason?.reason?.message || reason || 'unknown chunk load error'),
      at: Date.now(),
    };

    console.warn('[appRuntime] Chunk/module load issue detected.', windowObject.__LAST_CHUNK_LOAD_ERROR);

    await recoverFromStaleBuildImpl({
      reason: `chunk-load:${windowObject.location.pathname}`,
      locationObject: windowObject.location,
      sessionStorageObject,
    });
  };

  const finalizeVisibilityRestore = () => {
    restoreSavedScrollPosition({
      sessionStorageObject,
      windowObject,
      documentObject,
    });
    documentObject.documentElement.classList.remove('switching-tabs');
    visibilityChangeTimeout = null;
  };

  const handleVisibilityChange = async () => {
    try {
      if (visibilityChangeTimeout) {
        clearTimeout(visibilityChangeTimeout);
      }

      windowObject.__APP_STATE.isVisible = !documentObject.hidden;
      windowObject.__APP_STATE.lastVisibilityChange = Date.now();

      if (documentObject.hidden) {
        sessionStorageObject.setItem('scroll_position_before_hide', windowObject.scrollY.toString());
        sessionStorageObject.setItem('tab_hidden_at', Date.now().toString());

        if (documentObject.documentElement.classList.contains('ai-tutor-active')) {
          documentObject.documentElement.classList.add('switching-tabs');
        } else {
          documentObject.documentElement.classList.remove('switching-tabs');
        }
        return;
      }

      const buildCheckResult = await checkForUpdatedBuildImpl({
        reason: 'visibilitychange-visible',
        locationObject: windowObject.location,
        historyObject: windowObject.history,
        sessionStorageObject,
      });

      if (buildCheckResult?.status === 'refreshing') {
        return;
      }

      if (!documentObject.documentElement.classList.contains('ai-tutor-active')) {
        finalizeVisibilityRestore();
        return;
      }

      visibilityChangeTimeout = windowObject.setTimeout(finalizeVisibilityRestore, 100);
    } catch (error) {
      console.error('[appRuntime] Critical error in visibilitychange handler:', error);
    }
  };

  const handlePageShow = async (event) => {
    windowObject.__APP_STATE.isVisible = !documentObject.hidden;
    windowObject.__APP_STATE.lastVisibilityChange = Date.now();

    if (event.persisted) {
      const buildCheckResult = await checkForUpdatedBuildImpl({
        reason: 'pageshow-persisted',
        locationObject: windowObject.location,
        historyObject: windowObject.history,
        sessionStorageObject,
      });

      if (buildCheckResult?.status === 'refreshing') {
        return;
      }

      restoreSavedScrollPosition({
        sessionStorageObject,
        windowObject,
        documentObject,
      });
      documentObject.documentElement.classList.remove('switching-tabs');
    }

    windowObject.__APP_RUNNING = true;
  };

  const handleBeforeUnload = () => {
    try {
      sessionStorageObject.setItem('app_tab_closing', Date.now().toString());
      windowObject.__APP_RUNNING = false;
    } catch (error) {
      console.error('[appRuntime] Error in beforeunload handler:', error);
    }
  };

  const handlePreloadError = (event) => {
    event.preventDefault();
    void handleChunkLoadIssue(event?.payload || 'vite:preloadError');
  };

  const handleWindowError = (event) => {
    if (isChunkLoadError(event?.error || event?.message)) {
      void handleChunkLoadIssue(event?.error || event?.message);
    }
  };

  const handleUnhandledRejection = (event) => {
    if (isChunkLoadError(event?.reason)) {
      event.preventDefault();
      void handleChunkLoadIssue(event?.reason);
    }
  };

  windowObject.addEventListener('vite:preloadError', handlePreloadError);
  windowObject.addEventListener('error', handleWindowError);
  windowObject.addEventListener('unhandledrejection', handleUnhandledRejection);
  documentObject.addEventListener('visibilitychange', handleVisibilityChange);
  windowObject.addEventListener('pageshow', handlePageShow);
  windowObject.addEventListener('beforeunload', handleBeforeUnload);

  return () => {
    windowObject.removeEventListener('vite:preloadError', handlePreloadError);
    windowObject.removeEventListener('error', handleWindowError);
    windowObject.removeEventListener('unhandledrejection', handleUnhandledRejection);
    documentObject.removeEventListener('visibilitychange', handleVisibilityChange);
    windowObject.removeEventListener('pageshow', handlePageShow);
    windowObject.removeEventListener('beforeunload', handleBeforeUnload);
    if (visibilityChangeTimeout) {
      clearTimeout(visibilityChangeTimeout);
    }
  };
}

export function initializeAppRuntime() {
  const runtimeWindow = getRuntimeWindow();
  if (!runtimeWindow) {
    return () => {};
  }

  if (runtimeWindow.__APP_RUNTIME_CLEANUP__) {
    return runtimeWindow.__APP_RUNTIME_CLEANUP__;
  }

  const cleanup = installAppRuntime();
  runtimeWindow.__APP_RUNTIME_CLEANUP__ = cleanup;
  return cleanup;
}
